<template>
    <div>
        <div v-if="notes && notes.length">
            <div id="notes" :class="{'scroll-able-notes': scrollAble}">
                <div v-for="(note, index) in notesOrdered" :key="note.id" class="note">
                    <hr v-if="index"/>

                    <div class="flex-vertical-middle">
                        <person-box-user :id="note.creator" class="no-max-width">
                            <template #name="{username}">
                                <span>{{username}}</span>
                                <slot name="additionally_username" :note="note"/>
                            </template>

                            <template #default>
                                {{dateHelper('d.m.Y, H:i', note.created)}} Uhr <strong v-if="withEdited && note.edited !== null"><br>
                                ({{buildNoteEditedInfo(note)}})
                            </strong>
                            </template>
                        </person-box-user>

                        <div class="flex-grow"/>

                        <slot name="additionally" :note="note" :edit="getEditCallback(note)"/>
                    </div>

                    <div v-if="note.edit" class="spacer-15">
                        <form :id="`note-form-${note.id}`">
                            <form-text-editor :id="`note-text-${note.id}`" name="note" v-model="note.note" :config="editorConfig"/>

                            <slot name="noteFormAdditionally" :note="note"/>

                            <div class="spacer-15">
                                <button class="button button-save" @click.prevent="updateNote(getNoteFormData(note), note)">
                                    <i class="material-icons md-18" aria-hidden="true" role="img">save</i>
                                    <span v-if="isComment">Kommentar speichern</span>
                                    <span v-else>Notiz speichern</span>
                                </button>
                            </div>
                        </form>
                    </div>
                    <user-content v-else-if="withMentions" v-html="note.note" class="task-text spacer-15"/>
                    <div v-else v-html="note.note" class="task-text spacer-15"></div>

                    <slot name="entry" :note="note"/>

                    <div v-if="index === (notesOrdered.length - 1) && scrollAble" class="padding-bottom-15"/>
                </div>
            </div>

            <hr class="form-spacer"/>
        </div>
        <div v-else-if="!hideEmpty" class="text-grey">
            <slot name="empty">Für diesen Eintrag sind noch keine {{isComment ? 'Kommentare' : 'Notizen'}} vorhanden.</slot>

            <hr class="form-spacer"/>
        </div>

        <template v-if="addNoteForm">
            <form-row>
                <form-text-editor id="note-text" name="note" :config="editorConfig" ref="editor"/>
            </form-row>

            <slot name="form"/>
        </template>
        <template v-else>
            <form @submit.prevent ref="form">
                <form-row>
                    <form-text-editor id="note-text" name="note" :config="editorConfig" ref="editor"/>
                </form-row>

                <slot name="form"/>
            </form>

            <div class="spacer-15">
                <button class="button button-save" @click.prevent="addNote(getNoteFormData())">
                    <i class="material-icons md-18" aria-hidden="true" role="img">save</i>
                    <span v-if="isComment">Kommentar speichern</span>
                    <span v-else>Notiz speichern</span>
                </button>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
    import UserContent from '@/components/UserContent.vue';
    import {getFormData} from '@/library/form';
    import {hasTextContent} from '@/utils/portuna';
    import {FroalaOptions} from 'froala-editor';
    import {Component, Prop, Vue} from 'vue-facing-decorator';
    import TopMessageHandler from '../library/topMessageHandler';
    import dateHelper from '../utils/portuna/dateHelper';
    import FileList from './FileList.vue';
    import FormInputFile from './form/FormInputFile.vue';
    import FormRow from './form/FormRow.vue';
    import FormTextEditor from './form/FormTextEditor.vue';
    import PersonBoxUser from './PersonBoxUser.vue';

    type Note = {
        id: number;
        note: string;
        creator: number;
        created: string;
        editor: Nullable<number>;
        edited: Nullable<string>;
        edit?: boolean;
        additional_data: Nullable<string>;
    }

    @Component({
        name: 'NoteEditor',
        components: {
            UserContent,
            FileList,
            PersonBoxUser,
            FormInputFile,
            FormTextEditor,
            FormRow,
        },
        methods: {
            dateHelper,
        },
    })
    export default class NoteEditor extends Vue
    {
        readonly $refs: {
            form: HTMLFormElement
            editor: FormTextEditor
        };

        @Prop({
            type: Array,
            required: true,
        }) notes: Note[];

        @Prop({
            type: Object,
        }) editorConfig: Partial<FroalaOptions>;

        @Prop({
            type: Function,
            required: false,
        }) addNoteCallback: Function;

        @Prop({
            type: Function,
            required: false,
        }) updateNoteCallback: Function;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) addNoteForm: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) hideEmpty: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) withMentions: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) withEdited: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) scrollAble: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) isComment: boolean;

        edit: boolean = false;

        get notesOrdered()
        {
            return this.notes.sort((a, b) => a.id - b.id);
        }

        buildNoteEditedInfo(note: Note)
        {
            return 'Bearbeitet: ' + dateHelper('d.m.Y, H:i', note.edited) + ' Uhr' +
                (note.editor !== note.creator && note.editor !== null ? ' von ' + this.getUsername(note.editor) : '');
        }

        addNote(formData: { note: string })
        {
            if (typeof formData?.note === 'undefined' || !hasTextContent(formData.note))
            {
                TopMessageHandler.error('Bitte geben Sie zunächst ' + (this.isComment ? 'ein Kommentar' : 'eine Notiz') + ' an.');

                return false;
            }

            return this.addNoteCallback(formData);
        }

        updateNote(formData: { note: string }, note: Note)
        {
            if (typeof formData?.note === 'undefined' || !hasTextContent(formData.note))
            {
                TopMessageHandler.error('Bitte geben Sie zunächst ' + (this.isComment ? 'ein Kommentar' : 'eine Notiz') + ' an.');

                return false;
            }

            return this.updateNoteCallback(formData, note);
        }

        getEditCallback(note: Note)
        {
            return () =>
            {
                note['edit'] = note.edit ? !note.edit : true;
            };
        }

        getNoteFormData(note: Nullable<Note> = null)
        {
            let form = this.$refs.form;

            if (note)
            {
                form = document.getElementById(`note-form-${note.id}`) as HTMLFormElement;
            }

            return getFormData(form);
        }

        clearEditor()
        {
            this.$refs.editor.getEditor().html.set('');
        }
    }
</script>